/**
 * BprEngagementPage - /so/bpr/engagements/:engagementUuid
 *
 * Engagement workspace for one BPR engagement: header (status + pin-framework
 * action + pinned version badge) with tabbed sections for Sessions (incl.
 * per-session note capture), Synthesis (the pinned inverted triangle with
 * classified findings), Recommendations, and Outputs.
 */

import type {
  BprCreateRecommendationRequest,
  BprDiscoveryNote,
  BprEngagement,
  BprEngagementStatus,
  BprFinding,
  BprFrameworkContent,
  BprIssue,
  BprOutput,
  BprOutputKind,
  BprRecommendation,
  BprSession,
  BprSessionStatus,
} from '@protoss/shared';
import {
  BPR_ENGAGEMENT_STATUSES,
  BPR_SESSION_STATUSES,
  DEAL_DOCUMENT_ACCEPT_ATTR,
} from '@protoss/shared';
import type { PillVariant } from '@protoss/ui';
import {
  Button,
  Dialog,
  NumberInput,
  Pill,
  Select,
  SelectItem,
  Tabs,
  TextArea,
  TextField,
} from '@protoss/ui';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { PageErrorState } from '../components/shared/PageErrorState';
import { AppShell, MainContentShell, type RightRailItem, useModuleChatRailItem } from '../components/shell';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  BprFrameworkTriangle,
  bprIssueFindingKey,
} from '../features/bpr/components/BprFrameworkTriangle';
import { BprDiagramsSection } from '../features/bpr/components/BprDiagramsSection';
import { BprGraphSection } from '../features/bpr/components/BprGraphSection';
import { readBprSimpleSynthesisMetadata } from '@protoss/shared';
import { BprSimpleProblemsSection } from '../features/bpr/components/BprSimpleProblemsSection';
import { BprSynthesisRunsSummary } from '../features/bpr/components/BprSynthesisRunsSummary';
import { BprSimpleRecommendationsSection } from '../features/bpr/components/BprSimpleRecommendationsSection';
import { bprApi } from '../features/bpr/services/bprApi';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import { bprEngagementStatusPresentation } from './BprEngagementsPage';
import './bpr-page.css';

// ─── Presentation helpers ───────────────────────────────────────────────────

function sessionStatusVariant(status: BprSessionStatus): PillVariant {
  switch (status) {
    case 'planned':
      return 'neutral';
    case 'held':
      return 'accent';
    case 'processed':
      return 'success';
    case 'cancelled':
      return 'muted';
    default:
      return 'neutral';
  }
}

function recommendationStatusVariant(status: BprRecommendation['status']): PillVariant {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'rejected':
      return 'danger';
    default:
      return 'neutral';
  }
}

function outputKindLabel(kind: BprOutputKind): string {
  switch (kind) {
    case 'sow':
      return 'SOW';
    case 'estimate':
      return 'Estimate';
    case 'resource_plan':
      return 'Resource plan';
    case 'diagrams':
      return 'Diagrams';
    default:
      return kind;
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function findIssue(
  framework: BprFrameworkContent | null,
  issueKey: string
): { areaKey: string; issue: BprIssue } | null {
  if (!framework) return null;
  for (const area of framework.areas) {
    const issue = area.issues.find((i) => i.key === issueKey);
    if (issue) return { areaKey: area.key, issue };
  }
  return null;
}

/** Estimate output content shape written by the API's buildBprEstimateSections. */
interface BprEstimateContent {
  sections?: Array<{
    sectionKey?: string;
    label?: string;
    hours?: number;
    subsections?: Array<{ subsectionKey?: string; label?: string; hours?: number }>;
  }>;
  grandTotalHours?: number;
  notes?: string[];
}

// ─── Sessions section ────────────────────────────────────────────────────────

function SessionEditorDialog({
  engagementUuid,
  framework,
  session,
  open,
  onOpenChange,
  onSaved,
}: {
  engagementUuid: string;
  framework: BprFrameworkContent | null;
  /** Null = create a new session. */
  session: BprSession | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = React.useState('');
  const [functionalAreaKey, setFunctionalAreaKey] = React.useState('');
  const [scheduledAt, setScheduledAt] = React.useState('');
  const [durationHours, setDurationHours] = React.useState('');
  const [status, setStatus] = React.useState<BprSessionStatus>('planned');
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setTitle(session?.title ?? '');
    setFunctionalAreaKey(session?.functionalAreaKey ?? '');
    setScheduledAt(session?.scheduledAt ? session.scheduledAt.slice(0, 10) : '');
    setDurationHours(session?.durationHours != null ? String(session.durationHours) : '');
    setStatus(session?.status ?? 'planned');
    setError(null);
  }, [open, session]);

  const canSave = title.trim() !== '' && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError(null);
    try {
      const base = {
        title: title.trim(),
        ...(functionalAreaKey ? { functionalAreaKey } : {}),
        ...(scheduledAt ? { scheduledAt } : {}),
        ...(durationHours.trim() !== '' && Number(durationHours) > 0
          ? { durationHours: Number(durationHours) }
          : {}),
      };
      if (session) {
        await bprApi.updateSession(engagementUuid, session.uuid, { ...base, status });
      } else {
        await bprApi.createSession(engagementUuid, base);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save session.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !isSaving && onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content bpr-dialog">
          <Dialog.Title className="dialog-title">
            {session ? 'Edit session' : 'New discovery session'}
          </Dialog.Title>
          <div className="bpr-dialog__form">
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Order to cash walkthrough"
              required
            />
            <label className="bpr-dialog__field">
              <span className="bpr-dialog__field-label">Functional area</span>
              {framework ? (
                <Select
                  value={functionalAreaKey || undefined}
                  onValueChange={setFunctionalAreaKey}
                  placeholder="Select an area (optional)"
                >
                  {framework.areas.map((area) => (
                    <SelectItem key={area.key} value={area.key}>
                      {area.title}
                    </SelectItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  value={functionalAreaKey}
                  onChange={(e) => setFunctionalAreaKey(e.target.value)}
                  placeholder="e.g. order_to_cash (optional)"
                />
              )}
            </label>
            <TextField
              label="Scheduled date"
              type="date"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
            <NumberInput
              label="Duration (hours)"
              value={durationHours}
              onChange={(e) => setDurationHours(e.target.value)}
              min={0}
            />
            {session ? (
              <label className="bpr-dialog__field">
                <span className="bpr-dialog__field-label">Status</span>
                <Select
                  value={status}
                  onValueChange={(value) => setStatus(value as BprSessionStatus)}
                >
                  {BPR_SESSION_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </Select>
              </label>
            ) : null}
            {error ? <p className="bpr-dialog__error">{error}</p> : null}
          </div>
          <div className="bpr-dialog__actions">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={isSaving}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button type="button" size="sm" disabled={!canSave} onClick={() => void handleSave()}>
              {isSaving ? 'Saving…' : session ? 'Save session' : 'Create session'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SessionsSection({
  engagementUuid,
  framework,
  sessions,
  notes,
  onChanged,
}: {
  engagementUuid: string;
  framework: BprFrameworkContent | null;
  sessions: BprSession[];
  notes: BprDiscoveryNote[];
  onChanged: () => void;
}) {
  const [selectedSessionUuid, setSelectedSessionUuid] = React.useState<string | null>(null);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editorSession, setEditorSession] = React.useState<BprSession | null>(null);
  const [noteDraft, setNoteDraft] = React.useState('');
  const transcriptFileRef = React.useRef<HTMLInputElement | null>(null);
  const [isSavingNote, setIsSavingNote] = React.useState(false);
  const [isAttaching, setIsAttaching] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const selectedSession =
    sessions.find((s) => s.uuid === selectedSessionUuid) ?? sessions[0] ?? null;
  const sessionNotes = selectedSession
    ? notes.filter((n) => n.sessionUuid === selectedSession.uuid)
    : [];

  const handleAddNote = async () => {
    if (!selectedSession || noteDraft.trim() === '' || isSavingNote) return;
    setIsSavingNote(true);
    setActionError(null);
    try {
      await bprApi.createNote(engagementUuid, selectedSession.uuid, { rawText: noteDraft.trim() });
      setNoteDraft('');
      onChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save note.');
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleTranscriptUpload = async (file: File | undefined) => {
    if (!selectedSession || !file || isAttaching) return;
    setIsAttaching(true);
    setActionError(null);
    try {
      await bprApi.uploadSessionTranscript(engagementUuid, selectedSession.uuid, file);
      onChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to upload transcript.');
    } finally {
      setIsAttaching(false);
    }
  };

  return (
    <div className="bpr-sessions">
      <div className="bpr-sessions__list-pane">
        <div className="bpr-section__toolbar">
          <h2 className="bpr-section__heading">Sessions</h2>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setEditorSession(null);
              setEditorOpen(true);
            }}
          >
            New session
          </Button>
        </div>
        {sessions.length === 0 ? (
          <p className="bpr-section__empty">No discovery sessions yet.</p>
        ) : (
          <ul className="bpr-sessions__list">
            {sessions.map((session) => (
              <li key={session.uuid}>
                <button
                  type="button"
                  className={`bpr-sessions__item${
                    selectedSession?.uuid === session.uuid ? ' bpr-sessions__item--selected' : ''
                  }`}
                  onClick={() => setSelectedSessionUuid(session.uuid)}
                >
                  <span className="bpr-sessions__item-title">{session.title}</span>
                  <span className="bpr-sessions__item-meta">
                    {session.functionalAreaKey ?? 'unscoped'} · {formatDateTime(session.scheduledAt)}
                  </span>
                  <Pill variant={sessionStatusVariant(session.status)}>{session.status}</Pill>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedSession ? (
        <div className="bpr-sessions__detail-pane">
          <div className="bpr-section__toolbar">
            <h3 className="bpr-section__subheading">{selectedSession.title}</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setEditorSession(selectedSession);
                setEditorOpen(true);
              }}
            >
              Edit
            </Button>
          </div>
          <div className="bpr-sessions__transcript">
            {selectedSession.transcriptDocumentUuid ? (
              <Pill variant="success">Transcript attached</Pill>
            ) : (
              <>
                <input
                  ref={transcriptFileRef}
                  type="file"
                  accept={DEAL_DOCUMENT_ACCEPT_ATTR}
                  className="bpr-sessions__transcript-input"
                  aria-label="Upload session transcript"
                  onChange={(e) => {
                    void handleTranscriptUpload(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isAttaching}
                  onClick={() => transcriptFileRef.current?.click()}
                >
                  {isAttaching ? 'Uploading…' : 'Upload transcript'}
                </Button>
                <span className="bpr-sessions__transcript-hint">
                  .txt, .docx, .pdf, .vtt, .srt, or .json
                </span>
              </>
            )}
          </div>

          <div className="bpr-sessions__note-capture">
            <TextArea
              label="Capture note"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="What did you learn in this session?"
              rows={4}
            />
            <div className="bpr-sessions__note-actions">
              <Button
                type="button"
                size="sm"
                disabled={noteDraft.trim() === '' || isSavingNote}
                onClick={() => void handleAddNote()}
              >
                {isSavingNote ? 'Saving…' : 'Add note'}
              </Button>
            </div>
          </div>
          {actionError ? <p className="bpr-dialog__error">{actionError}</p> : null}

          <div className="bpr-sessions__notes">
            <h4 className="bpr-section__subheading">Notes ({sessionNotes.length})</h4>
            {sessionNotes.length === 0 ? (
              <p className="bpr-section__empty">No notes captured for this session yet.</p>
            ) : (
              <ul className="bpr-sessions__note-list">
                {sessionNotes.map((note) => (
                  <li key={note.uuid} className="bpr-sessions__note">
                    <div className="bpr-sessions__note-head">
                      <Pill variant={note.sourceType === 'transcript' ? 'accent' : 'ghost'}>
                        {note.sourceType}
                      </Pill>
                      <span className="bpr-sessions__note-date">
                        {formatDateTime(note.createdAt)}
                      </span>
                    </div>
                    <p className="bpr-sessions__note-text">{note.rawText}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}

      <SessionEditorDialog
        engagementUuid={engagementUuid}
        framework={framework}
        session={editorSession}
        open={editorOpen}
        onOpenChange={setEditorOpen}
        onSaved={() => {
          setEditorOpen(false);
          onChanged();
        }}
      />
    </div>
  );
}

// ─── Findings (Synthesis) ────────────────────────────────────────────────────

function AddFindingDialog({
  engagementUuid,
  sessions,
  target,
  onOpenChange,
  onSaved,
}: {
  engagementUuid: string;
  sessions: BprSession[];
  /** Null = closed; otherwise the issue the finding is classified onto. */
  target: { areaKey: string; issueKey: string } | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [sessionUuid, setSessionUuid] = React.useState('');
  const [summary, setSummary] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (target) {
      setSessionUuid('');
      setSummary('');
      setError(null);
    }
  }, [target]);

  const handleSave = async () => {
    if (!target || summary.trim() === '' || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      await bprApi.createFinding(engagementUuid, {
        functionalAreaKey: target.areaKey,
        issueKey: target.issueKey,
        currentStateSummary: summary.trim(),
        ...(sessionUuid ? { sessionUuid } : {}),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create finding.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={target !== null} onOpenChange={(next) => !isSaving && onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content bpr-dialog">
          <Dialog.Title className="dialog-title">Add finding</Dialog.Title>
          <Dialog.Description className="dialog-description">
            Classify a discovered current-state fact onto {target?.issueKey ?? 'this issue'}.
          </Dialog.Description>
          <div className="bpr-dialog__form">
            <label className="bpr-dialog__field">
              <span className="bpr-dialog__field-label">Session (optional)</span>
              <Select
                value={sessionUuid || undefined}
                onValueChange={setSessionUuid}
                placeholder="Select the source session"
              >
                {sessions.map((session) => (
                  <SelectItem key={session.uuid} value={session.uuid}>
                    {session.title}
                  </SelectItem>
                ))}
              </Select>
            </label>
            <TextArea
              label="Current-state summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={4}
              placeholder="What is the customer doing today, and what breaks?"
              required
            />
            {error ? <p className="bpr-dialog__error">{error}</p> : null}
          </div>
          <div className="bpr-dialog__actions">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={isSaving}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              size="sm"
              disabled={summary.trim() === '' || isSaving}
              onClick={() => void handleSave()}
            >
              {isSaving ? 'Saving…' : 'Add finding'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Recommendation editor ───────────────────────────────────────────────────

interface RecommendationDraft {
  /** Existing recommendation being edited, or null when creating. */
  recommendation: BprRecommendation | null;
  issueKey: string;
  patternKey: string;
  findingUuid?: string;
  title: string;
}

function RecommendationEditorDialog({
  engagementUuid,
  framework,
  draft,
  onOpenChange,
  onSaved,
}: {
  engagementUuid: string;
  framework: BprFrameworkContent | null;
  /** Null = closed. */
  draft: RecommendationDraft | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = React.useState('');
  const [futureStateProcess, setFutureStateProcess] = React.useState('');
  const [patternKey, setPatternKey] = React.useState('');
  const [estimatedHours, setEstimatedHours] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!draft) return;
    setTitle(draft.recommendation?.title ?? draft.title);
    setFutureStateProcess(draft.recommendation?.futureStateProcess ?? '');
    setPatternKey(draft.recommendation?.solutionPatternKey ?? draft.patternKey);
    setEstimatedHours(
      draft.recommendation?.estimatedHours != null ? String(draft.recommendation.estimatedHours) : ''
    );
    setError(null);
  }, [draft]);

  const issueMatch = draft ? findIssue(framework, draft.issueKey) : null;
  const patterns = issueMatch?.issue.patterns ?? [];

  const handleSave = async (statusOverride?: 'confirmed' | 'rejected') => {
    if (!draft || title.trim() === '' || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      const base: BprCreateRecommendationRequest = {
        issueKey: draft.issueKey,
        title: title.trim(),
        ...(draft.findingUuid ? { findingUuid: draft.findingUuid } : {}),
        ...(patternKey ? { solutionPatternKey: patternKey } : {}),
        ...(futureStateProcess.trim() !== ''
          ? { futureStateProcess: futureStateProcess.trim() }
          : {}),
        ...(estimatedHours.trim() !== '' && Number(estimatedHours) >= 0
          ? { estimatedHours: Number(estimatedHours) }
          : {}),
      };
      if (draft.recommendation) {
        await bprApi.updateRecommendation(engagementUuid, draft.recommendation.uuid, {
          ...base,
          ...(statusOverride ? { status: statusOverride } : {}),
        });
      } else {
        await bprApi.createRecommendation(engagementUuid, base);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save recommendation.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={draft !== null} onOpenChange={(next) => !isSaving && onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content bpr-dialog">
          <Dialog.Title className="dialog-title">
            {draft?.recommendation ? 'Edit recommendation' : 'New recommendation'}
          </Dialog.Title>
          <Dialog.Description className="dialog-description">
            Issue: {issueMatch?.issue.title ?? draft?.issueKey ?? ''}
          </Dialog.Description>
          <div className="bpr-dialog__form">
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
            <TextArea
              label="Future-state process"
              value={futureStateProcess}
              onChange={(e) => setFutureStateProcess(e.target.value)}
              rows={4}
              placeholder="How the process should work after implementation"
            />
            <label className="bpr-dialog__field">
              <span className="bpr-dialog__field-label">Solution pattern</span>
              {patterns.length > 0 ? (
                <Select
                  value={patternKey || undefined}
                  onValueChange={setPatternKey}
                  placeholder="Select a pattern"
                >
                  {patterns.map((pattern) => (
                    <SelectItem key={pattern.key} value={pattern.key}>
                      {pattern.title}
                    </SelectItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  value={patternKey}
                  onChange={(e) => setPatternKey(e.target.value)}
                  placeholder="Pattern key (no pinned framework patterns found)"
                />
              )}
            </label>
            <NumberInput
              label="Estimated hours"
              value={estimatedHours}
              onChange={(e) => setEstimatedHours(e.target.value)}
              min={0}
            />
            {error ? <p className="bpr-dialog__error">{error}</p> : null}
          </div>
          <div className="bpr-dialog__actions">
            {draft?.recommendation && draft.recommendation.status !== 'rejected' ? (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={isSaving}
                onClick={() => void handleSave('rejected')}
              >
                Reject
              </Button>
            ) : null}
            {draft?.recommendation && draft.recommendation.status !== 'confirmed' ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={isSaving}
                onClick={() => void handleSave('confirmed')}
              >
                Confirm
              </Button>
            ) : null}
            <span className="bpr-dialog__actions-spacer" />
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={isSaving}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              size="sm"
              disabled={title.trim() === '' || isSaving}
              onClick={() => void handleSave()}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function RecommendationsSection({
  framework,
  recommendations,
  onEdit,
}: {
  framework: BprFrameworkContent | null;
  recommendations: BprRecommendation[];
  onEdit: (draft: RecommendationDraft) => void;
}) {
  return (
    <div className="bpr-recommendations">
      <div className="bpr-section__toolbar">
        <h2 className="bpr-section__heading">Recommendations</h2>
      </div>
      {recommendations.length === 0 ? (
        <p className="bpr-section__empty">
          No recommendations yet. Create them from findings on the Synthesis tab.
        </p>
      ) : (
        <ul className="bpr-recommendations__list">
          {recommendations.map((rec) => {
            const issueMatch = findIssue(framework, rec.issueKey);
            const pattern = issueMatch?.issue.patterns.find(
              (p) => p.key === rec.solutionPatternKey
            );
            return (
              <li key={rec.uuid} className="bpr-recommendations__item">
                <div className="bpr-recommendations__item-head">
                  <span className="bpr-recommendations__item-title">{rec.title}</span>
                  <Pill variant={recommendationStatusVariant(rec.status)}>{rec.status}</Pill>
                  <span className="bpr-recommendations__item-hours">
                    {rec.estimatedHours != null ? `${rec.estimatedHours}h` : 'no estimate'}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onEdit({
                        recommendation: rec,
                        issueKey: rec.issueKey,
                        patternKey: rec.solutionPatternKey ?? '',
                        title: rec.title,
                      })
                    }
                  >
                    Edit
                  </Button>
                </div>
                <div className="bpr-recommendations__item-meta">
                  {issueMatch?.issue.title ?? rec.issueKey}
                  {pattern ? ` · ${pattern.title}` : ''}
                </div>
                {rec.futureStateProcess ? (
                  <p className="bpr-recommendations__item-body">{rec.futureStateProcess}</p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── Outputs section ─────────────────────────────────────────────────────────

function OutputViewer({ output }: { output: BprOutput }) {
  if (output.kind === 'sow' && output.contentMarkdown) {
    return (
      <div className="bpr-outputs__markdown">
        <ReactMarkdown>{output.contentMarkdown}</ReactMarkdown>
      </div>
    );
  }
  if (output.kind === 'estimate' && output.content) {
    const content = output.content as BprEstimateContent;
    const sections = content.sections ?? [];
    return (
      <div className="bpr-outputs__estimate">
        {sections.map((section, idx) => (
          <div key={section.sectionKey ?? idx} className="bpr-outputs__estimate-section">
            <div className="bpr-outputs__estimate-row bpr-outputs__estimate-row--section">
              <span>{section.label ?? section.sectionKey ?? 'Section'}</span>
              <span>{section.hours ?? 0}h</span>
            </div>
            {(section.subsections ?? []).map((sub, subIdx) => (
              <div key={sub.subsectionKey ?? subIdx} className="bpr-outputs__estimate-row">
                <span>{sub.label ?? 'Item'}</span>
                <span>{sub.hours ?? 0}h</span>
              </div>
            ))}
          </div>
        ))}
        <div className="bpr-outputs__estimate-row bpr-outputs__estimate-row--total">
          <span>Total</span>
          <span>{content.grandTotalHours ?? 0}h</span>
        </div>
        {(content.notes ?? []).length > 0 ? (
          <ul className="bpr-outputs__estimate-notes">
            {(content.notes ?? []).map((note, idx) => (
              <li key={idx}>{note}</li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }
  // Resource plan (and any unknown shape): raw JSON fallback.
  return (
    <pre className="bpr-outputs__raw">
      {JSON.stringify(output.content ?? output.contentMarkdown ?? null, null, 2)}
    </pre>
  );
}

function OutputsSection({
  engagementUuid,
  outputs,
  onChanged,
}: {
  engagementUuid: string;
  outputs: BprOutput[];
  onChanged: () => void;
}) {
  const [generatingKind, setGeneratingKind] = React.useState<BprOutputKind | null>(null);
  const [selectedUuid, setSelectedUuid] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const selectedOutput = outputs.find((o) => o.uuid === selectedUuid) ?? outputs[0] ?? null;

  const handleGenerate = async (kind: BprOutputKind) => {
    if (generatingKind) return;
    setGeneratingKind(kind);
    setError(null);
    try {
      const output = await bprApi.generateOutput(engagementUuid, kind);
      setSelectedUuid(output.uuid);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate output.');
    } finally {
      setGeneratingKind(null);
    }
  };

  return (
    <div className="bpr-outputs">
      <div className="bpr-section__toolbar">
        <h2 className="bpr-section__heading">Outputs</h2>
        <div className="bpr-outputs__generate">
          {(['estimate', 'resource_plan', 'sow'] as const).map((kind) => (
            <Button
              key={kind}
              type="button"
              variant="outline"
              size="sm"
              disabled={generatingKind !== null}
              onClick={() => void handleGenerate(kind)}
            >
              {generatingKind === kind ? 'Generating…' : `Generate ${outputKindLabel(kind)}`}
            </Button>
          ))}
        </div>
      </div>
      {error ? <p className="bpr-dialog__error">{error}</p> : null}
      {outputs.length === 0 ? (
        <p className="bpr-section__empty">
          No outputs generated yet. Confirm recommendations first, then generate deliverables.
        </p>
      ) : (
        <div className="bpr-outputs__layout">
          <ul className="bpr-outputs__list">
            {outputs.map((output) => (
              <li key={output.uuid}>
                <button
                  type="button"
                  className={`bpr-outputs__item${
                    selectedOutput?.uuid === output.uuid ? ' bpr-outputs__item--selected' : ''
                  }`}
                  onClick={() => setSelectedUuid(output.uuid)}
                >
                  <span className="bpr-outputs__item-kind">{outputKindLabel(output.kind)}</span>
                  <span className="bpr-outputs__item-meta">
                    v{output.version} · {formatDateTime(output.generatedAt)}
                  </span>
                  <Pill variant={output.status === 'generated' ? 'success' : 'muted'}>
                    {output.status}
                  </Pill>
                </button>
              </li>
            ))}
          </ul>
          {selectedOutput ? (
            <div className="bpr-outputs__viewer">
              <OutputViewer output={selectedOutput} />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

type EngagementTab = 'sessions' | 'graph' | 'diagrams' | 'synthesis' | 'recommendations' | 'outputs';

export function BprEngagementPage() {
  const { engagementUuid = '' } = useParams<{ engagementUuid: string }>();
  const navigate = useNavigate();
  const { workspace, isLoadingWorkspace } = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const moduleChatItem = useModuleChatRailItem();

  const [engagement, setEngagement] = React.useState<BprEngagement | null>(null);
  const [framework, setFramework] = React.useState<BprFrameworkContent | null>(null);
  const [sessions, setSessions] = React.useState<BprSession[]>([]);
  const [notes, setNotes] = React.useState<BprDiscoveryNote[]>([]);
  const [findings, setFindings] = React.useState<BprFinding[]>([]);
  const [recommendations, setRecommendations] = React.useState<BprRecommendation[]>([]);
  const [outputs, setOutputs] = React.useState<BprOutput[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<EngagementTab>('sessions');
  const [isPinning, setIsPinning] = React.useState(false);
  const [pinError, setPinError] = React.useState<string | null>(null);
  const [findingTarget, setFindingTarget] = React.useState<{
    areaKey: string;
    issueKey: string;
  } | null>(null);
  const [recDraft, setRecDraft] = React.useState<RecommendationDraft | null>(null);

  const loadAll = React.useCallback(async () => {
    if (!engagementUuid) return;
    setError(null);
    try {
      const [
        loadedEngagement,
        loadedFramework,
        loadedSessions,
        loadedNotes,
        loadedFindings,
        loadedRecommendations,
        loadedOutputs,
      ] = await Promise.all([
        bprApi.getEngagement(engagementUuid),
        bprApi.getPinnedFramework(engagementUuid).catch(() => null),
        bprApi.listSessions(engagementUuid),
        bprApi.listNotes(engagementUuid),
        bprApi.listFindings(engagementUuid),
        bprApi.listRecommendations(engagementUuid),
        bprApi.listOutputs(engagementUuid),
      ]);
      setEngagement(loadedEngagement);
      setFramework(loadedFramework);
      setSessions(loadedSessions);
      setNotes(loadedNotes);
      setFindings(loadedFindings);
      setRecommendations(loadedRecommendations);
      setOutputs(loadedOutputs);
    } catch {
      setError('Failed to load the engagement.');
    } finally {
      setLoading(false);
    }
  }, [engagementUuid]);

  React.useEffect(() => {
    setLoading(true);
    void loadAll();
  }, [loadAll]);

  const findingsByIssue = React.useMemo(() => {
    const map = new Map<string, BprFinding[]>();
    for (const finding of findings) {
      const key = bprIssueFindingKey(finding.functionalAreaKey, finding.issueKey);
      const list = map.get(key) ?? [];
      list.push(finding);
      map.set(key, list);
    }
    return map;
  }, [findings]);

  const isSimpleMode =
    workspace?.bprSynthesisMode === 'simple' ||
    findings.some((f) => f.problemMeta != null);

  const hasSimpleRecs =
    isSimpleMode || recommendations.some((r) => readBprSimpleSynthesisMetadata(r) != null);

  const handlePinFramework = async () => {
    if (isPinning) return;
    setIsPinning(true);
    setPinError(null);
    try {
      await bprApi.pinFramework(engagementUuid);
      await loadAll();
    } catch (err) {
      setPinError(err instanceof Error ? err.message : 'Failed to pin framework.');
    } finally {
      setIsPinning(false);
    }
  };

  const handleStatusChange = async (status: BprEngagementStatus) => {
    try {
      const updated = await bprApi.updateEngagement(engagementUuid, { status });
      setEngagement(updated);
    } catch (err) {
      setPinError(err instanceof Error ? err.message : 'Failed to update status.');
    }
  };

  if (isLoadingWorkspace) return null;

  const leftNav = (
    <AppNav
      selected="bpr"
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const rightRailItems: RightRailItem[] = moduleChatItem ? [moduleChatItem] : [];

  const statusPresentation = engagement
    ? bprEngagementStatusPresentation(engagement.status)
    : null;

  const titleMeta = engagement ? (
    <span className="bpr-engagement__title-meta">
      {statusPresentation ? (
        <Pill variant={statusPresentation.variant}>{statusPresentation.label}</Pill>
      ) : null}
      {engagement.frameworkVersionUuid ? (
        <Pill variant="accent">
          Framework pinned {formatDateTime(engagement.frameworkPinnedAt)}
        </Pill>
      ) : (
        <Pill variant="ghost">No framework pinned</Pill>
      )}
    </span>
  ) : null;

  const headerActions = engagement ? (
    <>
      {engagement.dealUuid ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/so/deal/${engagement.dealUuid}`)}
        >
          Open deal
        </Button>
      ) : null}
      <label className="bpr-engagement__status-control">
        <span className="bpr-engagement__status-label">Status</span>
        <Select
          value={engagement.status}
          onValueChange={(value) => void handleStatusChange(value as BprEngagementStatus)}
          size="sm"
        >
          {BPR_ENGAGEMENT_STATUSES.map((status) => (
            <SelectItem key={status} value={status}>
              {status}
            </SelectItem>
          ))}
        </Select>
      </label>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isPinning}
        onClick={() => void handlePinFramework()}
      >
        {isPinning
          ? 'Pinning…'
          : engagement.frameworkVersionUuid
            ? 'Re-pin framework'
            : 'Pin framework'}
      </Button>
    </>
  ) : null;

  const main = (
    <MainContentShell
      title={engagement?.title ?? 'BPR engagement'}
      breadcrumb={`Business Process Reviews${engagement?.companyName ? ` > ${engagement.companyName}` : ''}`}
      titleMeta={titleMeta}
      actions={headerActions}
      loading={loading}
      subnav={
        <Tabs
          items={[
            { value: 'sessions', label: `Sessions (${sessions.length})` },
            { value: 'graph', label: 'Knowledge graph' },
            { value: 'diagrams', label: 'Diagrams' },
            { value: 'synthesis', label: 'Synthesis' },
            { value: 'recommendations', label: `Recommendations (${recommendations.length})` },
            { value: 'outputs', label: `Outputs (${outputs.length})` },
          ]}
          value={tab}
          onValueChange={(value) => setTab(value as EngagementTab)}
          ariaLabel="Engagement sections"
        />
      }
    >
      {error ? (
        <PageErrorState message={error} onRetry={() => void loadAll()} />
      ) : (
        <div className="bpr-engagement__body">
          {pinError ? <p className="bpr-dialog__error">{pinError}</p> : null}
          {tab === 'sessions' ? (
            <SessionsSection
              engagementUuid={engagementUuid}
              framework={framework}
              sessions={sessions}
              notes={notes}
              onChanged={() => void loadAll()}
            />
          ) : null}
          {/* key: remount on engagement change so a slow load/build for the
              previous engagement can never paint (or lock) this one's tab. */}
          {tab === 'graph' ? (
            <BprGraphSection key={engagementUuid} engagementUuid={engagementUuid} />
          ) : null}
          {/* key: remount per engagement (same rationale as the graph tab). */}
          {tab === 'diagrams' ? (
            <BprDiagramsSection key={engagementUuid} engagementUuid={engagementUuid} />
          ) : null}
          {tab === 'synthesis' ? (
            isSimpleMode ? (
              <div className="bpr-synthesis">
                <BprSynthesisRunsSummary engagementUuid={engagementUuid} />
                <BprSimpleProblemsSection
                  engagementUuid={engagementUuid}
                  findings={findings}
                  onChanged={() => void loadAll()}
                />
                {framework ? (
                  <div className="bpr-synthesis__framework-wrap">
                    <h3 className="bpr-section__subheading bpr-synthesis__framework-title">
                      Framework Triangle Mapping
                    </h3>
                    <BprFrameworkTriangle
                      content={framework}
                      findingsByIssue={findingsByIssue}
                      onAddFinding={(target) => setFindingTarget(target)}
                      onCreateRecommendation={({ issueKey, patternKey, findingUuid, suggestedTitle }) =>
                        setRecDraft({
                          recommendation: null,
                          issueKey,
                          patternKey: patternKey ?? '',
                          findingUuid,
                          title: suggestedTitle ?? '',
                        })
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : framework ? (
              <div className="bpr-synthesis">
                <p className="bpr-synthesis__hint">
                  Findings are matched onto the pinned framework's issues; confidence badges show
                  classification confidence.
                </p>
                <BprFrameworkTriangle
                  content={framework}
                  findingsByIssue={findingsByIssue}
                  onAddFinding={(target) => setFindingTarget(target)}
                  onCreateRecommendation={({ issueKey, patternKey, findingUuid, suggestedTitle }) =>
                    setRecDraft({
                      recommendation: null,
                      issueKey,
                      patternKey: patternKey ?? '',
                      findingUuid,
                      title: suggestedTitle ?? '',
                    })
                  }
                />
              </div>
            ) : (
              <div className="bpr-section__empty bpr-synthesis__unpinned">
                <p>
                  No framework pinned yet. Pin the workspace's approved framework to unlock the
                  synthesis view.
                </p>
                <Button
                  type="button"
                  size="sm"
                  disabled={isPinning}
                  onClick={() => void handlePinFramework()}
                >
                  {isPinning ? 'Pinning…' : 'Pin framework'}
                </Button>
              </div>
            )
          ) : null}
          {tab === 'recommendations' ? (
            hasSimpleRecs ? (
              <BprSimpleRecommendationsSection
                engagementUuid={engagementUuid}
                recommendations={recommendations}
                findings={findings}
                framework={framework}
                onEdit={(draft) => setRecDraft(draft)}
                onChanged={() => void loadAll()}
              />
            ) : (
              <RecommendationsSection
                framework={framework}
                recommendations={recommendations}
                onEdit={(draft) => setRecDraft(draft)}
              />
            )
          ) : null}
          {tab === 'outputs' ? (
            <OutputsSection
              engagementUuid={engagementUuid}
              outputs={outputs}
              onChanged={() => void loadAll()}
            />
          ) : null}
        </div>
      )}
    </MainContentShell>
  );

  return (
    <>
      <AppShell leftNav={leftNav} main={main} rightRailItems={rightRailItems} />
      <AddFindingDialog
        engagementUuid={engagementUuid}
        sessions={sessions}
        target={findingTarget}
        onOpenChange={(open) => {
          if (!open) setFindingTarget(null);
        }}
        onSaved={() => {
          setFindingTarget(null);
          void loadAll();
        }}
      />
      <RecommendationEditorDialog
        engagementUuid={engagementUuid}
        framework={framework}
        draft={recDraft}
        onOpenChange={(open) => {
          if (!open) setRecDraft(null);
        }}
        onSaved={() => {
          setRecDraft(null);
          void loadAll();
        }}
      />
    </>
  );
}
